
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Card from '../../components/base/Card';
import Button from '../../components/base/Button';
import { featuredChefs, chefSpecialties, southAfricanCities } from '../../mocks/chefs';

const ChefsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [availableOnly, setAvailableOnly] = useState(false);

  const filteredChefs = featuredChefs.filter(chef => {
    const matchesSearch = chef.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         chef.specialty.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSpecialty = !selectedSpecialty || chef.cuisines.some(cuisine => 
      cuisine.toLowerCase().includes(selectedSpecialty.toLowerCase()));
    const matchesLocation = !selectedLocation || chef.location.includes(selectedLocation);
    const matchesAvailability = !availableOnly || chef.available;

    return matchesSearch && matchesSpecialty && matchesLocation && matchesAvailability;
  });

  useEffect(() => {
    // Update page title and meta description
    document.title = 'Browse Private Chefs South Africa | SERVDFRESH Professional Chef Directory';
    
    // Add Schema.org JSON-LD
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Browse Private Chefs",
      "description": "Browse our curated selection of professional private chefs across South Africa. Each chef is vetted for quality, experience, and exceptional service.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/chefs`,
      "mainEntity": {
        "@type": "ItemList",
        "name": "Private Chefs Directory",
        "description": "Professional private chefs available for hire across South Africa",
        "numberOfItems": featuredChefs.length,
        "itemListElement": featuredChefs.map((chef, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "item": {
            "@type": "Person",
            "name": chef.name,
            "jobTitle": "Private Chef",
            "description": chef.description,
            "image": chef.image,
            "address": {
              "@type": "PostalAddress",
              "addressLocality": chef.location,
              "addressCountry": "ZA"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": chef.rating,
              "reviewCount": chef.reviews
            }
          }
        }))
      }
    });
    document.head.appendChild(schemaScript);

    return () => {
      // Cleanup
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-emerald-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Find Your Perfect Private Chef
            </h1>
            <p className="text-xl max-w-3xl mx-auto">
              Browse our curated selection of professional private chefs across South Africa. 
              Each chef is vetted for quality, experience, and exceptional service.
            </p>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="bg-white py-8 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search chefs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
              />
              <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"></i>
            </div>

            {/* Specialty Filter */}
            <div>
              <button className="w-full flex items-center justify-between px-4 py-2 border border-gray-300 rounded-lg text-left text-sm pr-8">
                {selectedSpecialty || 'All Specialties'}
                <i className="ri-arrow-down-s-line"></i>
              </button>
            </div>

            {/* Location Filter */}
            <div>
              <button className="w-full flex items-center justify-between px-4 py-2 border border-gray-300 rounded-lg text-left text-sm pr-8">
                {selectedLocation || 'All Locations'}
                <i className="ri-arrow-down-s-line"></i>
              </button>
            </div>

            {/* Availability Filter */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="available"
                checked={availableOnly}
                onChange={(e) => setAvailableOnly(e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="available" className="text-sm text-gray-700">Available only</label>
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900">
              {filteredChefs.length} Chef{filteredChefs.length !== 1 ? 's' : ''} Found
            </h2>
          </div>

          {/* Chef Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredChefs.map((chef) => (
              <Card key={chef.id} className="overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="relative">
                  <img
                    src={chef.image}
                    alt={chef.name}
                    className="w-full h-64 object-cover object-top"
                  />
                  <div className="absolute top-4 left-4 bg-emerald-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                    Featured
                  </div>
                  <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-sm font-medium ${
                    chef.available
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {chef.available ? 'Available' : 'Unavailable'}
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-1">{chef.name}</h3>
                      <p className="text-emerald-600 font-medium">{chef.specialty}</p>
                    </div>
                    <div className="bg-blue-100 text-blue-800 p-1 rounded-full">
                      <i className="ri-verified-badge-fill text-sm"></i>
                    </div>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-gray-600 text-sm">
                      <i className="ri-map-pin-line mr-2"></i>
                      {chef.location}
                    </div>
                    <div className="flex items-center text-gray-600 text-sm">
                      <i className="ri-time-line mr-2"></i>
                      {chef.experience}
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <i className="ri-star-fill text-yellow-400 mr-1"></i>
                        <span className="font-medium">{chef.rating}</span>
                        <span className="text-gray-500 text-sm ml-1">({chef.reviews})</span>
                      </div>
                      <span className="text-emerald-600 font-semibold">{chef.price}</span>
                    </div>
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">{chef.description}</p>
                  
                  <div className="flex space-x-3">
                    <Link 
                      to={`/chef/${chef.slug}`}
                      className="flex-1"
                    >
                      <Button className="w-full whitespace-nowrap">
                        View Profile
                      </Button>
                    </Link>
                    <Button variant="outline" size="sm" className="whitespace-nowrap">
                      <i className="ri-message-line"></i>
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ChefsPage;
